import {
  detachLayoutProps,
  WSElement,
  WSEmptyState,
  WSFileInput,
  WSFlexBox,
  WSForm,
  WSIcon,
  WSInputText,
  WSList,
  WSMarginProps,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  VaultFileKind,
  VaultFileVisibility
} from "@wingspanhq/wingspan-api-sdk";
import React, { ChangeEvent, useImperativeHandle, useState } from "react";
import * as Yup from "yup";

import styles from "./styles.module.scss";
import { queriesAccounts } from "@query";
import { useSession } from "@hooks/useSession";
import { ErrorAlert } from "../../../../v3/components/ErrorAlert";
import { useMutationUploadVaultFile } from "../../../../v3/utils/useMutationUploadVaultFile";

interface FormBusinessProfileValues {
  logoFileId?: string;
  websiteUrl?: string;
  linkedInUrl?: string;
  instagramUrl?: string;
  xUrl?: string;
  otherUrl?: string;
}

export interface FormBusinessProfileProps extends WSMarginProps {
  setLoading?: (loading: boolean) => void;
  onSuccess?: () => void;
}
export type FormBusinessProfileRef =
  // Define any custom props/methods to expose via ref
  HTMLFormElement | null;
function FormBusinessProfileFC(
  props: FormBusinessProfileProps,
  ref: React.Ref<FormBusinessProfileRef>
) {
  const { setLoading, onSuccess, ...otherProps } = props;
  const session = useSession();
  const businessProfileFormRef = React.useRef<HTMLFormElement>(null);
  const { layoutProps } = detachLayoutProps(otherProps);
  const [fileToUpload, setFileToUpload] = useState<File>();

  const [uploadFile, uploadFileMeta] = useMutationUploadVaultFile();
  const [updateAccount, updateAccountMeta] =
    queriesAccounts.updateAccount.useMutation({
      dependencies: [
        queriesAccounts.getAccount.queryKeyBase,
        queriesAccounts.listAccounts.queryKeyBase
      ]
    });

  useImperativeHandle(ref, () => {
    return {
      requestSubmit: () => {
        businessProfileFormRef.current?.requestSubmit();
      }
    } as FormBusinessProfileRef;
  }, []);

  const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    event.preventDefault();
    uploadFileMeta.reset();
    if (event.target && event.target.files && event.target.files) {
      const logoFile = event.target.files[0];
      setFileToUpload(logoFile);
    }
  };

  const onSubmit = async (data: FormBusinessProfileValues) => {
    uploadFileMeta.reset();
    updateAccountMeta.reset();
    setLoading?.(true);
    let vaultFile;
    if (fileToUpload) {
      vaultFile = await uploadFile({
        file: fileToUpload,
        kind: VaultFileKind.GENERIC,
        visibility: VaultFileVisibility.PUBLIC
      });

      // A failed upload resolves undefined and surfaces in the error alert —
      // saving the rest of the profile here would hide it behind onSuccess.
      if (!vaultFile) {
        setLoading?.(false);
        return;
      }
    }
    await updateAccount(
      {
        accountId: session.accountId,
        accountUpdate: {
          profile: {
            logoFileId: vaultFile?.id,
            company: {
              website: data.websiteUrl || undefined,
              links: {
                websiteUrl: data.websiteUrl || undefined,
                linkedInUrl: data.linkedInUrl || undefined,
                instagramUrl: data.instagramUrl || undefined,
                twitterUrl: data.xUrl || undefined,
                otherUrl: data.otherUrl || undefined
              }
            }
          }
        }
      },
      {
        onSuccess: () => {
          onSuccess?.();
        }
      }
    );
    setLoading?.(false);
  };

  return (
    <WSElement className={styles.container} {...layoutProps}>
      <WSForm<FormBusinessProfileValues>
        ref={businessProfileFormRef}
        defaultValues={{
          logoFileId: "",
          websiteUrl: "",
          linkedInUrl: "",
          instagramUrl: "",
          xUrl: "",
          otherUrl: ""
        }}
        validationSchema={Yup.object().shape({
          logoFileId: Yup.string(),
          websiteUrl: Yup.string().url("Invalid URL"),
          linkedInUrl: Yup.string().url("Invalid URL"),
          instagramUrl: Yup.string().url("Invalid URL"),
          xUrl: Yup.string().url("Invalid URL"),
          otherUrl: Yup.string().url("Invalid URL")
        })}
        onSubmit={onSubmit}
      >
        <WSList gap="XL">
          <WSElement>
            <WSText.ParagraphSm mb="S" color="gray500">
              Logo
            </WSText.ParagraphSm>
            {fileToUpload ? (
              <WSElement mb="XL" p="XL" className={styles.selectedFile}>
                <WSFlexBox.CenterY wrap="nowrap">
                  <WSText className={styles.selectedFileName} singleLine>
                    {fileToUpload.name}
                  </WSText>
                  <WSIcon
                    block
                    color="gray500"
                    size="M"
                    mx="M"
                    name="exit"
                    onClick={() => {
                      setFileToUpload(undefined);
                      uploadFileMeta.reset();
                    }}
                  />
                </WSFlexBox.CenterY>
              </WSElement>
            ) : (
              <WSFileInput name="logoFileId" onChange={onFileChange}>
                <WSEmptyState
                  type="images"
                  title="No logo yet"
                  description="Only PNG/JPG formats supported. Images with 120px height works best. If your image is too large, we'll try to resize it for you."
                  orientation="horizontal"
                  size="M"
                  hasBorder
                  actions={{
                    buttons: [
                      {
                        className: styles.addLogoBtn,
                        label: "Add logo",
                        kind: "Secondary",
                        type: "button"
                      }
                    ]
                  }}
                />
              </WSFileInput>
            )}
          </WSElement>

          <WSForm.Field
            name="websiteUrl"
            component={WSInputText}
            label="Business website URL (optional)"
            componentProps={{
              placeholder: "https://"
            }}
          />

          <WSForm.Field
            name="linkedInUrl"
            component={WSInputText}
            label="LinkedIn URL (optional)"
            componentProps={{
              placeholder: "https://"
            }}
          />

          <WSForm.Field
            name="instagramUrl"
            component={WSInputText}
            label="Instagram URL (optional)"
            componentProps={{
              placeholder: "https://"
            }}
          />

          <WSForm.Field
            name="xUrl"
            component={WSInputText}
            label="X URL (optional)"
            componentProps={{
              placeholder: "https://"
            }}
          />

          <WSForm.Field
            name="otherUrl"
            component={WSInputText}
            label="Other websites (optional)"
            componentProps={{
              helperText:
                "Include any websites, help articles, or resources that would benefit your contractors",
              placeholder: "https://"
            }}
          />

          <ErrorAlert context="BusinessProfile" error={uploadFileMeta.error} />

          <ErrorAlert
            context="BusinessProfile"
            error={updateAccountMeta.error}
          />
        </WSList>
      </WSForm>
    </WSElement>
  );
}

export const FormBusinessProfile = React.forwardRef<
  FormBusinessProfileRef,
  FormBusinessProfileProps
>(FormBusinessProfileFC);
